<template lang="pug">
  #family-safety-confirm-template-edit(v-cloak)
    .box
      .box-header.with-border.register-header
        .title
          span 安否情報提供（家族）の事前設定
      .box-body.register-body
        .note
          span 宛先・案内内容・情報提供項目を事前に設定することができます（情報提供項目は事前設定が必須です）。
          br
          span 宛先用の宛先グループは、利用者等管理の宛先グループで作成してください。
        //- 宛先種別
        InputTargetTypeForFamily(v-model="targetType")
        //- 宛先種別：グループ 宛先選択モーダル表示ボタン
        TargetSelectButton(v-if="targetType == 'group'" v-model="groups" :isBusy="isBusy" @onTargetBtn="onTargetBtn")

        FormTextarea(
          key-name="メッセージ内容"
          name="text"
          v-model="openingSentence"
          placeholder="家族に送るメッセージ内容を入力してください。\n\n例：\n安否情報提供のテストを実施します。\nご覧になったら「確認しました」を押してください。"
          :validation-rules="{ max: 300 }"
          :maxlength="300"
          :styles="{ maxWidth: '760px', width: '760px', minHeight: '160px' }")

        // 利用者個別状況
        .confirm-list.d-flex.flex-column.ml-2
          span.resident-status.ml-2 情報提供項目：利用者個別状況
          .content.d-flex.flex-row.align-items-center(v-for="item in residentConfirms")
            .confirm.flex-grow-1
              .title {{ item.label }}
              .text {{ item.text }}
              .text(v-if="item.inputType == 0") 選択肢：{{ item.choicesLabel }}
              .text(v-else-if="item.inputType == 1") 自由記述
            .buttons.d-flex.flex-row-reverse
              PermittedFunctionSwitch(v-model="item.use")
        // 施設全体状況
        .mb-4
        .confirm-list.d-flex.flex-column.ml-2
          span.facility-status.ml-2 情報提供項目：施設全体状況
          .content.d-flex.flex-row.align-items-center(v-for="item in facilityConfirms")
            .confirm.flex-grow-1
              .title {{ item.label }}
              .text {{ item.text }}
              .text(v-if="item.inputType == 0") 選択肢：{{ item.choicesLabel }}
              .text(v-else-if="item.inputType == 1") 自由記述
            .buttons.d-flex.flex-row-reverse
              PermittedFunctionSwitch(v-model="item.use")

        .register-row(v-show="errorMessage")
          .register-group
            .register-value
              .error-message {{ errorMessage }}

      .box-footer.with-border.register-footer
        .buttons
          button.btn.update-btn(@click="showConfirmUpdateModal" type="button") 更新
          button.btn.cancel-btn(@click="gotoSafetyConfirmList" type="button") キャンセル

    OkCancelModal(v-model="showUpdateModal" title="安否情報提供（家族）の事前設定を更新しますか？" @ok="doUpdate")
    OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoSafetyConfirmList")

    GroupSelectModal(ref="groupSelectModal" :groupType.sync="groupType" @add="addSendGroupTarget")

    PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";
import FamilySafetyConfirmsTemplatesService, {
  FamilySafetyConfirmsTemplatesGetModel,
  FamilySafetyConfirmsTemplatesPatchModel
} from "@/commons/services/FamilySafetyConfirmsTemplates";
import SendMessageGroupsService, { GroupType, SendMessageGroupsGetModel } from "@/commons/services/SendMessageGroups";
import FamilySafetyConfirmMastersService, {
  FamilySafetyConfirmMastersGetModel,
  FamilySafetyConfirmMasterRadioInputOption,
  FamilySafetyConfirmMasterType
} from "@/commons/services/FamilySafetyConfirmMasters";
import FamiliesService, { FamilyGetModel } from "@/commons/services/Families";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";
import PermittedFunctionSwitch from "@/pc/basics/PermittedFunctionSwitch.vue";
import TargetSelectButton, { GroupModel, ResidentGroupTargetModel, SendMessageGroupTargetModel } from "@/pc/containers/safetyConfirms/TargetSelectButton.vue";
import GroupSelectModal from "@/pc/containers/safetyConfirms/GroupSelectModal.vue";
import InputTargetTypeForFamily from "@/pc/containers/safetyConfirms/InputTargetTypeForFamily.vue";

/**
 * 使用機能の確認画面コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    TargetSelectButton,
    GroupSelectModal,
    InputTargetTypeForFamily,
    FormTextarea,
    PermittedFunctionSwitch,
    PageLoading
  }
})
export default class FamilyTemplateEdit extends Vue {
  /** 安否確認(家族)テンプレートサービス */
  @Inject("FamilySafetyConfirmsTemplatesService") familySafetyConfirmsTemplatesService!: FamilySafetyConfirmsTemplatesService;
  /** 安否確認(家族)で送信する質問内容のマスタサービス */
  @Inject("FamilySafetyConfirmMastersService") familySafetyConfirmMastersService!: FamilySafetyConfirmMastersService;
  /** 宛先グループサービス */
  @Inject("SendMessageGroupsService") sendMessageGroupsService!: SendMessageGroupsService;
  /** 家族サービス */
  @Inject("FamiliesService") familiesService!: FamiliesService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** 安否確認(家族)で送信する質問内容のマスタ */
  masters: Array<FamilySafetyConfirmMastersGetModel> = [];
  /** 安否確認(家族)テンプレート */
  template: FamilySafetyConfirmsTemplatesGetModel | null = null;

  /** 宛先種別 */
  targetType: "all" | "group" | "" = "";
  /** 宛先種別がグループの場合の宛先候補 */
  groups: GroupModel = {
    familyGroups: [],
    otherUserGroups: [],
    residentGroups: [],
    otherUserGroupMemberGroups: []
  };
  /** グループ種別 */
  groupType: GroupType = GroupType.UNKNOWN;
  /** 冒頭文 */
  openingSentence: string | null = "";
  /** 施設状況質問リスト */
  facilityConfirms: Array<UseId> = [];
  /** 利用者状況質問リスト */
  residentConfirms: Array<UseId> = [];

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面情報作成
   *
   * - 更新対象の安否確認(家族)テンプレートを取得する
   * - テンプレートの使用する質問のMAPを作る
   * - 宛先グループを取得する
   * - 取得したグループから宛先グループ選択肢を作成する
   * - 安否確認(家族)で送信する質問内容のマスタを取得する
   * - 質問のMAP、質問内容のマスタから質問の使用する/しないの入力欄を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // 宛先選択肢のグループ情報を作成する
    const allFamily: Array<FamilyGetModel> = await this.getNonmaskedFamilies();
    const sendMessageGroups: Array<SendMessageGroupsGetModel> = await this.getSendMessageGroups();
    const residentGroups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const familyMapByResidentGroupId: Map<number, Array<FamilyGetModel>> = this.createFamilyMap(allFamily);
    this.groups = await this.createTargetGroups(sendMessageGroups, residentGroups, familyMapByResidentGroupId);

    const template: FamilySafetyConfirmsTemplatesGetModel = await this.getTemplate();
    const useIdMap: Map<number, boolean> = this.createUseIdMap(template);
    this.template = template;
    this.targetType = template.targetType ? template.targetType : "";
    // 宛先情報の復元は分岐が必要
    // v3.33.0以前のバージョンは宛先グループのgroupId（単体）を保持している
    // v3.34.0以降のバージョンは宛先情報（複数）を保持している
    // 宛先情報（複数）が存在する場合はv3.34.0以降のデータと判断し、存在せずgroupIdが存在する場合はv3.33.0以前のデータと判断する
    if (template.targets) {
      this.restoreTargetsByLatest(template.targets, this.groups);
    } else if (template.groupId != null) {
      this.restoreTargetsByOld(template, this.groups);
    }
    this.openingSentence = template.openingSentence ? template.openingSentence : null;

    const masters: Array<FamilySafetyConfirmMastersGetModel> = await this.getFamilySafetyConfirmMasters();
    this.masters = masters;

    const residentConfirms: Array<UseId> = this.createResidentConfirms(masters, useIdMap);
    this.residentConfirms = residentConfirms;
    const facilityConfirms: Array<UseId> = this.createFacilityConfirms(masters, useIdMap);
    this.facilityConfirms = facilityConfirms;

    this.isBusy = false;

    return;
  }

  /**
   * ### 宛先で選択候補とする家族を取得する
   */
  public async getNonmaskedFamilies(): Promise<Array<FamilyGetModel>> {
    let response: Array<FamilyGetModel> = [];

    try {
      const params: any = {
        masking: "none"
      };
      response = await this.familiesService.getsInFacility(userStore, params);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const pagePerCount: number = 50;
        const count: number = await this.familiesService.getCountInFacility(userStore);
        const loopEnd: number = Math.ceil(count / pagePerCount);

        for (let i = 0; i < loopEnd; i++) {
          const params: any = {
            startIndex: i * pagePerCount,
            endIndex: i * pagePerCount + pagePerCount,
            masking: "none"
          };
          response = response.concat(await this.familiesService.getsInFacility(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### 宛先グループを取得する
   */
  public async getSendMessageGroups(): Promise<Array<SendMessageGroupsGetModel>> {
    const response: Array<SendMessageGroupsGetModel> = await this.sendMessageGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 家族MAPを作成する
   *
   * - key：利用者グループid、value：家族の配列
   */
  private createFamilyMap(allFamily: Array<FamilyGetModel>): Map<number, Array<FamilyGetModel>> {
    const map: Map<number, Array<FamilyGetModel>> = new Map();
    for (const family of allFamily) {
      const families: Array<FamilyGetModel> | undefined = map.get(family.groupId);
      if (families) {
        families.push(family);
        map.set(family.groupId, families);
      } else {
        map.set(family.groupId, [family]);
      }
    }
    return map;
  }

  /**
   * ### 宛先のグループ情報を作成する
   */
  private async createTargetGroups(
    sendMessageGroups: Array<SendMessageGroupsGetModel>,
    residentGroups: Array<ResidentGroupsGetModel>,
    familyMapByResidentGroupId: Map<number, Array<FamilyGetModel>>
  ): Promise<GroupModel> {
    // 宛先グループの情報作成
    const targetFamilyGroups: Array<SendMessageGroupTargetModel> = [];
    for (const group of sendMessageGroups) {
      const _group: SendMessageGroupTargetModel = Object.assign({}, group) as SendMessageGroupTargetModel;
      _group.isTarget = false;
      _group.memberCount = group.members.length;
      if (_group.type == GroupType.FAMILY) {
        targetFamilyGroups.push({
          ..._group,
          show: true
        });
      }
    }
    // 利用者グループの情報を作成
    const targetResidentGroups: Array<ResidentGroupTargetModel> = [];
    for (const group of residentGroups) {
      const _group: ResidentGroupTargetModel = Object.assign({}, group) as ResidentGroupTargetModel;
      _group.isTarget = false;
      const families: Array<FamilyGetModel> | undefined = familyMapByResidentGroupId.get(_group.id);
      _group.memberCount = families ? families.length : 0;
      targetResidentGroups.push({
        ..._group,
        key: `residentGroup_${_group.id}`,
        show: true
      });
    }

    return {
      familyGroups: targetFamilyGroups,
      otherUserGroupMemberGroups: [],
      residentGroups: targetResidentGroups,
      otherUserGroups: []
    };
  }

  /**
   * ### 安否確認(家族)テンプレートを取得する
   *
   * - 現時点ではテンプレートは1件のみなのでid指定は1固定
   */
  public async getTemplate(): Promise<FamilySafetyConfirmsTemplatesGetModel> {
    const response: FamilySafetyConfirmsTemplatesGetModel = await this.familySafetyConfirmsTemplatesService.get(userStore, 1);
    return response;
  }

  /**
   * ### 宛先を復元する（v3.33.0以前）
   *
   * - 宛先グループIDで検索して一致するものを宛先対象とする
   * - v3.33.0以前は宛先グループ（家族）しか宛先に設定できなかったの検索対象はfamilyGroupsのみ
   */
  private restoreTargetsByOld(template: FamilySafetyConfirmsTemplatesGetModel, groups: GroupModel): void {
    if (template.groupId == 0) {
      this.targetType = "all";
    }
    for (const group of groups.familyGroups) {
      if (group.id == template.groupId) {
        group.isTarget = true;
        this.targetType = "group";
        return;
      }
    }
    return;
  }

  /**
   * ### 宛先を復元する（v3.34.0以降）
   *
   * - ソートキーリストで検索して一致するものを宛先対象とする
   * - 宛先グループ（家族）、利用者グループのみが対象となる
   */
  private restoreTargetsByLatest(targets: string[], groups: GroupModel): void {
    // 検索用にmapにする
    const map: Map<string, string> = new Map<string, string>();
    for (const target of targets) {
      map.set(target, target);
    }

    for (const group of groups.familyGroups) {
      if (map.has(String(group.id))) {
        group.isTarget = true;
        this.targetType = "group";
      }
    }
    for (const group of groups.residentGroups) {
      if (map.has(group.key)) {
        group.isTarget = true;
        this.targetType = "group";
      }
    }
    return;
  }

  /**
   * ### テンプレートの使用する質問のMAPを作る
   *
   * - テンプレートの使用するIDリストに格納されている質問のみ使用する
   */
  public createUseIdMap(template: FamilySafetyConfirmsTemplatesGetModel): Map<number, boolean> {
    const map: Map<number, boolean> = new Map<number, boolean>();
    for (const useId of template.useIds) {
      map.set(useId, true);
    }
    return map;
  }

  /**
   * ### 安否確認(家族)で送信する質問内容のマスタを取得する
   */
  public async getFamilySafetyConfirmMasters(): Promise<Array<FamilySafetyConfirmMastersGetModel>> {
    const response: Array<FamilySafetyConfirmMastersGetModel> = await this.familySafetyConfirmMastersService.get(userStore);
    return response;
  }

  /**
   * ### 入力させる使用する質問リストを作成する
   *
   * - 入力種別がラジオボタンの質問は選択肢ラベルを作成する
   * - テンプレート情報から質問を使用するかしないかを設定する
   */
  public createResidentConfirms(masters: Array<FamilySafetyConfirmMastersGetModel>, useIdMap: Map<number, boolean>): Array<UseId> {
    const confirms: Array<UseId> = [];
    for (const master of masters) {
      if (master.type != FamilySafetyConfirmMasterType.RESIDENT) {
        continue;
      }

      const choicesLabels: string[] = [];
      if (master.inputType == 0) {
        const options: FamilySafetyConfirmMasterRadioInputOption = master.inputOptions as FamilySafetyConfirmMasterRadioInputOption;
        for (const choice of options.choices) {
          choicesLabels.push(choice.label);
        }
      }

      const use: boolean | undefined = useIdMap.get(master.id);
      const useId: UseId = {
        ...master,
        choicesLabel: choicesLabels.join("／"),
        use: use == true ? true : false
      };
      confirms.push(useId);
    }

    return confirms;
  }

  /**
   * ### 入力させる使用する質問リストを作成する
   *
   * - 入力種別がラジオボタンの質問は選択肢ラベルを作成する
   * - テンプレート情報から質問を使用するかしないかを設定する
   */
  public createFacilityConfirms(masters: Array<FamilySafetyConfirmMastersGetModel>, useIdMap: Map<number, boolean>): Array<UseId> {
    let confirms: Array<UseId> = [];

    for (const master of masters) {
      if (master.type != FamilySafetyConfirmMasterType.FACILITY) {
        continue;
      }

      let choicesLabel: string = "";
      if (master.inputType == 0) {
        const options: FamilySafetyConfirmMasterRadioInputOption = master.inputOptions as FamilySafetyConfirmMasterRadioInputOption;
        for (const choice of options.choices) {
          choicesLabel += "／" + choice.label;
        }
        // 先頭に「／」が入っているので、消す
        choicesLabel = choicesLabel.slice(1);
      }

      const use: boolean | undefined = useIdMap.get(master.id);
      const useId: UseId = {
        ...master,
        choicesLabel: choicesLabel,
        use: use == true ? true : false
      };
      confirms.push(useId);
    }

    return confirms;
  }

  /**
   * ### 宛先モーダルを表示する
   */
  onTargetBtn(): void {
    // メモリ渡しだと値変更が共有されてしまう
    // 値変更後に「キャンセル」された場合に困るので、コピーしたものを渡す
    const copy: GroupModel = JSON.parse(JSON.stringify(this.groups));
    const modal: any = this.$refs.groupSelectModal;
    modal.show(copy);
    return;
  }

  /**
   * ### 宛先モーダルの情報を更新する
   */
  addSendGroupTarget(targets: GroupModel): void {
    this.groups = targets;
    return;
  }

  /**
   * ### 更新確認モーダルを表示する
   *
   * - 表示前にバリデーションチェックして、エラーならモーダルは表示しない
   */
  public async showConfirmUpdateModal(): Promise<void> {
    this.showUpdateModal = true;
    return;
  }

  /**
   * ### 安否確認(家族)テンプレートを更新する
   *
   * - リクエストボディを作成する
   * - 安否確認(家族)テンプレートサービスを使用して更新する
   * - 更新成功ダイアログを表示する
   */
  public async doUpdate(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const requestBody: FamilySafetyConfirmsTemplatesPatchModel = this.createRequestBody();
      await this.familySafetyConfirmsTemplatesService.patch(userStore, 1, requestBody);

      this.showUpdateSuccessModal = true;
    } catch (error) {
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 施設更新パラメータ作成
   *
   * - useIdsは「使用する」の質問内容のみにする
   */
  public createRequestBody(): FamilySafetyConfirmsTemplatesPatchModel {
    const useIds: Array<number> = [];
    for (const useId of this.facilityConfirms) {
      if (useId.use == true) {
        useIds.push(useId.id);
      }
    }
    for (const useId of this.residentConfirms) {
      if (useId.use == true) {
        useIds.push(useId.id);
      }
    }

    const targets: string[] = [];
    for (const group of this.groups.familyGroups) {
      if (group.isTarget) {
        targets.push(String(group.id));
      }
    }
    for (const group of this.groups.residentGroups) {
      if (group.isTarget) {
        targets.push(String(group.key));
      }
    }

    const params: FamilySafetyConfirmsTemplatesPatchModel = {
      useIds: useIds,
      targetType: this.targetType as "all" | "group",
      targets: targets,
      openingSentence: this.openingSentence,
      updated: this.template ? this.template.updated : 0
    };

    return params;
  }

  /**
   * ### 安否確認一覧画面に遷移する
   */
  public gotoSafetyConfirmList(): void {
    this.$router.push(PcUrl.SafetyConfirmList);
    return;
  }
}

interface UseId extends FamilySafetyConfirmMastersGetModel {
  /** 使用するかしないか */
  use: boolean;
  /** ラジオボタンの場合の選択肢 */
  choicesLabel: string;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

// 親要素からネストしてスタイルを指定する
#family-safety-confirm-template-edit {
  max-width: 820px;
  margin-left: auto;
  margin-right: auto;

  .note {
    margin: 0 0 8px 11px;
  }

  .message {
    margin-bottom: 15px;
  }

  .confirm-list {
    width: 98%;

    .resident-status,
    .facility-status {
      font-size: 18px;
      font-weight: bold;
      margin: 0 0 4px 0;
    }

    .content {
      width: 100%;
      padding-top: 5px;
      padding-bottom: 5px;
      padding-left: 11px;
      padding-right: 11px;
      margin-bottom: 15px;
      border-bottom: solid 1px #cccccc;
      .confirm {
        .title {
          font-size: 18px;
          font-weight: bold;
        }
        .text {
          font-size: 14px;
        }
      }
      // ON/OFFスイッチ
      .buttons {
        width: 130px;
      }
    }
    .content:last-child {
      border-bottom: 0;
      margin-bottom: 0;
    }
  }
  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
